import { FC, memo } from 'react'

import { Text, Link } from '@cash-me/react-components'
import NextImage from 'next/image'

import { Section } from 'components/general-styles/General'
import { Grid, Col } from 'components/grid'
import { Carousel } from 'src/presentation/components/carousel'
import useWindowSize from 'src/presentation/hooks/useWindowSize'

import * as S from './styles'

const ICONS_PATH = `${process.env.NEXT_PUBLIC_SITE_IMAGES_PREFIX}/images/site/security-fraud/icons`

const SafetyTip: FC = memo(() => {
  const { isDesktop } = useWindowSize()

  return (
    <Section>
      <Grid col={{ phone: 1, tablet: 1, desktop: 12 }}>
        <Col col={{ desktop: 7 }} start={{ desktop: 2 }}>
          <S.Title>
            <Text as="h2" size="heading3" color="var(--colors-neutral-text-default)" weight="medium">
              Dicas de{' '}
              <Text as="span" size="heading3" color="var(--colors-pink-text-default)" weight="medium">
                segurança
              </Text>
            </Text>
          </S.Title>
        </Col>
        <Col col={{ desktop: 10 }} start={{ desktop: 2 }}>
          <Text color="var(--colors-neutral-text-weak)">
            Assim como na vida real, o ambiente digital exige uma série de cuidados e comportamentos responsáveis para
            evitar cair em golpes e fraudes.
          </Text>
        </Col>
        {isDesktop ? (
          <Col col={{ desktop: 10 }} start={{ desktop: 2 }}>
            <S.CardWrapper>
              <S.Card>
                <S.IconImage>
                  <NextImage
                    src={`${ICONS_PATH}/security-personal-data.svg`}
                    alt="Dados pessoais"
                    width="48"
                    height="48"
                    sizes="100vw"
                    style={{
                      width: '100%',
                      height: 'auto'
                    }}
                  />
                </S.IconImage>
                <Text as="h3" weight="medium" color="var(--colors-neutral-text-default)">
                  Dados pessoais
                </Text>
                <Text color="var(--colors-neutral-text-weak)" size="body2">
                  Evite compartilhar informações pessoais, como endereço, número de telefone ou dados bancários, nas
                  redes sociais ou em sites desconhecidos.
                </Text>
              </S.Card>
              <S.Card>
                <S.IconImage>
                  <NextImage
                    src={`${ICONS_PATH}/notebook-password.svg`}
                    alt="Senhas e códigos de segurança"
                    width="48"
                    height="48"
                    sizes="100vw"
                    style={{
                      width: '100%',
                      height: 'auto'
                    }}
                  />
                </S.IconImage>
                <Text as="h3" weight="medium" color="var(--colors-neutral-text-default)">
                  Senhas e códigos de segurança
                </Text>
                <Text color="var(--colors-neutral-text-weak)" size="body2">
                  Nunca informe senhas e códigos de segurança ou autenticação a ninguém.
                </Text>
              </S.Card>
              <S.Card>
                <S.IconImage>
                  <NextImage
                    src={`${ICONS_PATH}/pointer-click.svg`}
                    alt="Ofertas tentadoras"
                    width="48"
                    height="48"
                    sizes="100vw"
                    style={{
                      width: '100%',
                      height: 'auto'
                    }}
                  />
                </S.IconImage>
                <Text as="h3" weight="medium" color="var(--colors-neutral-text-default)">
                  Ofertas tentadoras
                </Text>
                <Text color="var(--colors-neutral-text-weak)" size="body2">
                  Desconfie de ofertas tentadoras que prometem altos ganhos e vantagens. Em caso de dúvida, busque mais
                  informações e pesquise.
                </Text>
              </S.Card>
              <S.Card>
                <S.IconImage>
                  <NextImage
                    src={`${ICONS_PATH}/email-compromised.svg`}
                    alt="Links e anexos de e-mails e mensagens"
                    width="48"
                    height="48"
                    sizes="100vw"
                    style={{
                      width: '100%',
                      height: 'auto'
                    }}
                  />
                </S.IconImage>
                <Text as="h3" weight="medium" color="var(--colors-neutral-text-default)">
                  Links e anexos de e-mails e mensagens
                </Text>
                <Text color="var(--colors-neutral-text-weak)" size="body2">
                  Não abra links ou anexos de e-mails ou mensagens de remetentes desconhecidos, porque podem direcionar
                  para sites maliciosos ou instalar malwares no seu dispositivo.
                </Text>
              </S.Card>
              <S.Card>
                <S.IconImage>
                  <NextImage
                    src={`${ICONS_PATH}/pointer-password.svg`}
                    alt="Senhas fortes e únicas"
                    width="48"
                    height="48"
                    sizes="100vw"
                    style={{
                      width: '100%',
                      height: 'auto'
                    }}
                  />
                </S.IconImage>
                <Text as="h3" weight="medium" color="var(--colors-neutral-text-default)">
                  Senhas fortes e únicas
                </Text>
                <Text color="var(--colors-neutral-text-weak)" size="body2">
                  Evite senhas fáceis de adivinhar, como datas de aniversário ou nomes de familiares. Use uma combinação
                  de letras maiúsculas e minúsculas, números e caracteres especiais.
                </Text>
              </S.Card>
              <S.Card>
                <S.IconImage>
                  <NextImage
                    src={`${ICONS_PATH}/notebook-security.svg`}
                    alt="Páginas suspeitas"
                    width="48"
                    height="48"
                    sizes="100vw"
                    style={{
                      width: '100%',
                      height: 'auto'
                    }}
                  />
                </S.IconImage>
                <Text as="h3" weight="medium" color="var(--colors-neutral-text-default)">
                  Páginas suspeitas
                </Text>
                <Text color="var(--colors-neutral-text-weak)" size="body2">
                  Verifique se há um cadeado na barra de endereços e se a URL do site inicia com &quot;https://&quot;.
                  Páginas seguras geralmente estão atualizadas e livres de erros. Desconfie da solicitações de dados
                  sensíveis sem motivo aparente.
                </Text>
              </S.Card>
              <S.Card>
                <S.IconImage>
                  <NextImage
                    src={`${ICONS_PATH}/safe-payment.svg`}
                    alt="Pagamentos antecipados para obtenção de crédito"
                    width="48"
                    height="48"
                    sizes="100vw"
                    style={{
                      width: '100%',
                      height: 'auto'
                    }}
                  />
                </S.IconImage>
                <Text as="h3" weight="medium" color="var(--colors-neutral-text-default)">
                  Pagamentos antecipados para obtenção de crédito
                </Text>
                <Text color="var(--colors-neutral-text-weak)" size="body2">
                  Evite realizar acordos com instituições que cobram taxas antecipadas para a aprovação de crédito.
                </Text>
              </S.Card>
              <S.Card>
                <S.IconImage>
                  <NextImage
                    src={`${ICONS_PATH}/safe-payment.svg`}
                    alt="Código de barras"
                    width="48"
                    height="48"
                    sizes="100vw"
                    style={{
                      width: '100%',
                      height: 'auto'
                    }}
                  />
                </S.IconImage>
                <Text as="h3" weight="medium" color="var(--colors-neutral-text-default)">
                  Código de barras
                </Text>
                <Text color="var(--colors-neutral-text-weak)" size="body2">
                  Confira se os últimos dígitos do código de barras correspondem ao valor a ser pago e se os primeiros
                  coincidem com o código do banco emissor.{' '}
                  <Link
                    href="https://portal.febraban.org.br/pagina/3164/12/pt-br/associados/"
                    target="_blank"
                    size="default"
                    inline
                  >
                    <Text color="var(--colors-neutral-text-weak)" size="body2" as="span">
                      Consulte o número do banco aqui.
                    </Text>
                  </Link>
                </Text>
              </S.Card>
              <S.Card>
                <S.IconImage>
                  <NextImage
                    src={`${ICONS_PATH}/safe-payment.svg`}
                    alt="Beneficiário desconhecido"
                    width="48"
                    height="48"
                    sizes="100vw"
                    style={{
                      width: '100%',
                      height: 'auto'
                    }}
                  />
                </S.IconImage>
                <Text as="h3" weight="medium" color="var(--colors-neutral-text-default)">
                  Beneficiário desconhecido
                </Text>
                <Text color="var(--colors-neutral-text-weak)" size="body2">
                  Verifique o nome da empresa ao pagar online. Beneficiário no boleto deve coincidir com o da transação.
                  Confirme dados para evitar erros.
                </Text>
              </S.Card>
            </S.CardWrapper>
          </Col>
        ) : (
          <Carousel navigation={true}>
            <S.CarouselItem>
              <S.IconImage>
                <NextImage
                  src={`${ICONS_PATH}/security-personal-data.svg`}
                  alt="Telefone"
                  width="48"
                  height="48"
                  sizes="100vw"
                  style={{
                    width: '100%',
                    height: 'auto'
                  }}
                />
              </S.IconImage>
              <Text as="h3" weight="medium" color="var(--colors-neutral-text-default)">
                Dados pessoais
              </Text>
              <Text color="var(--colors-neutral-text-weak)" size="body2">
                Evite compartilhar informações pessoais, como endereço, número de telefone ou dados bancários, nas redes
                sociais ou em sites desconhecidos.
              </Text>
            </S.CarouselItem>
            <S.CarouselItem>
              <S.IconImage>
                <NextImage
                  src={`${ICONS_PATH}/notebook-password.svg`}
                  alt="Telefone"
                  width="48"
                  height="48"
                  sizes="100vw"
                  style={{
                    width: '100%',
                    height: 'auto'
                  }}
                />
              </S.IconImage>
              <Text as="h3" weight="medium" color="var(--colors-neutral-text-default)">
                Senhas e códigos de segurança
              </Text>
              <Text color="var(--colors-neutral-text-weak)" size="body2">
                Nunca informe senhas e códigos de segurança ou autenticação a ninguém.
              </Text>
            </S.CarouselItem>
            <S.CarouselItem>
              <S.IconImage>
                <NextImage
                  src={`${ICONS_PATH}/pointer-click.svg`}
                  alt="Telefone"
                  width="48"
                  height="48"
                  sizes="100vw"
                  style={{
                    width: '100%',
                    height: 'auto'
                  }}
                />
              </S.IconImage>
              <Text as="h3" weight="medium" color="var(--colors-neutral-text-default)">
                Ofertas tentadoras
              </Text>
              <Text color="var(--colors-neutral-text-weak)" size="body2">
                Desconfie de ofertas tentadoras que prometem altos ganhos e vantagens. Em caso de dúvida, busque mais
                informações e pesquise.
              </Text>
            </S.CarouselItem>
            <S.CarouselItem>
              <S.IconImage>
                <NextImage
                  src={`${ICONS_PATH}/email-compromised.svg`}
                  alt="Telefone"
                  width="48"
                  height="48"
                  sizes="100vw"
                  style={{
                    width: '100%',
                    height: 'auto'
                  }}
                />
              </S.IconImage>
              <Text as="h3" weight="medium" color="var(--colors-neutral-text-default)">
                Links e anexos de e-mails e mensagens
              </Text>
              <Text color="var(--colors-neutral-text-weak)" size="body2">
                Não abra links ou anexos de e-mails ou mensagens de remetentes desconhecidos, porque podem direcionar
                para sites maliciosos ou instalar malwares no seu dispositivo.
              </Text>
            </S.CarouselItem>
            <S.CarouselItem>
              <S.IconImage>
                <NextImage
                  src={`${ICONS_PATH}/pointer-password.svg`}
                  alt="Telefone"
                  width="48"
                  height="48"
                  sizes="100vw"
                  style={{
                    width: '100%',
                    height: 'auto'
                  }}
                />
              </S.IconImage>
              <Text as="h3" weight="medium" color="var(--colors-neutral-text-default)">
                Senhas fortes e únicas
              </Text>
              <Text color="var(--colors-neutral-text-weak)" size="body2">
                Evite senhas fáceis de adivinhar, como datas de aniversário ou nomes de familiares. Use uma combinação
                de letras maiúsculas e minúsculas, números e caracteres especiais.
              </Text>
            </S.CarouselItem>
            <S.CarouselItem>
              <S.IconImage>
                <NextImage
                  src={`${ICONS_PATH}/notebook-security.svg`}
                  alt="Telefone"
                  width="48"
                  height="48"
                  sizes="100vw"
                  style={{
                    width: '100%',
                    height: 'auto'
                  }}
                />
              </S.IconImage>
              <Text as="h3" weight="medium" color="var(--colors-neutral-text-default)">
                Páginas suspeitas
              </Text>
              <Text color="var(--colors-neutral-text-weak)" size="body2">
                Verifique se há um cadeado na barra de endereços e se a URL do site inicia com &quot;https://&quot;.
                Páginas seguras geralmente estão atualizadas e livres de erros. Desconfie da solicitações de dados
                sensíveis sem motivo aparente.
              </Text>
            </S.CarouselItem>
            <S.CarouselItem>
              <S.IconImage>
                <NextImage
                  src={`${ICONS_PATH}/safe-payment.svg`}
                  alt="Telefone"
                  width="48"
                  height="48"
                  sizes="100vw"
                  style={{
                    width: '100%',
                    height: 'auto'
                  }}
                />
              </S.IconImage>
              <Text as="h3" weight="medium" color="var(--colors-neutral-text-default)">
                Pagamentos antecipados para obtenção de crédito
              </Text>
              <Text color="var(--colors-neutral-text-weak)" size="body2">
                Evite realizar acordos com instituições que cobram taxas antecipadas para a aprovação de crédito.
              </Text>
            </S.CarouselItem>
            <S.CarouselItem>
              <S.IconImage>
                <NextImage
                  src={`${ICONS_PATH}/safe-payment.svg`}
                  alt="Telefone"
                  width="48"
                  height="48"
                  sizes="100vw"
                  style={{
                    width: '100%',
                    height: 'auto'
                  }}
                />
              </S.IconImage>
              <Text as="h3" weight="medium" color="var(--colors-neutral-text-default)">
                Código de barras
              </Text>
              <Text color="var(--colors-neutral-text-weak)" size="body2">
                Confira se os últimos dígitos do código de barras correspondem ao valor a ser pago e se os primeiros
                coincidem com o código do banco emissor.{' '}
                <Link href="https://portal.febraban.org.br/pagina/3164/12/pt-br/associados/" target="_blank" inline>
                  Consulte o número do banco aqui.
                </Link>
              </Text>
            </S.CarouselItem>
            <S.CarouselItem>
              <S.IconImage>
                <NextImage
                  src={`${ICONS_PATH}/safe-payment.svg`}
                  alt="Telefone"
                  width="48"
                  height="48"
                  sizes="100vw"
                  style={{
                    width: '100%',
                    height: 'auto'
                  }}
                />
              </S.IconImage>
              <Text as="h3" weight="medium" color="var(--colors-neutral-text-default)">
                Beneficiário desconhecido
              </Text>
              <Text color="var(--colors-neutral-text-weak)" size="body2">
                Verifique o nome da empresa ao pagar online. Beneficiário no boleto deve coincidir com o da transação.
                Confirme dados para evitar erros.
              </Text>
            </S.CarouselItem>
          </Carousel>
        )}
      </Grid>
    </Section>
  )
})

SafetyTip.displayName = 'SafetyTip'

export default SafetyTip
